// src/core/hooks/useInfiniteSelectList.ts
import { useCallback, useEffect, useRef, useState } from "react"

export interface Page<T> {
  items: T[]
  total: number
}

export interface InfiniteSelectListParams {
  limit: number
  offset: number
  search?: string
}

export interface InfiniteSelectListOptions<T> {
  fetchPage: (params: InfiniteSelectListParams) => Promise<Page<T>>
  getId: (item: T) => string | number
  pageSize?: number
  searchDebounceMs?: number
  initialSearch?: string
}

export interface InfiniteSelectListState<T> {
  items: T[]
  total: number
  hasMore: boolean
  isFetching: boolean
  error: unknown
  search: string
  setSearch: (s: string) => void
  loadMore: () => void
  reload: () => void
}

/**
 * Бесконечный список для дропдаунов: первая страница на монтировании,
 * догрузка пачками (loadMore), серверный поиск с debounce.
 *
 * Защита от гонок: монотонный seqRef — ответ устаревшего запроса
 * (после смены поиска/reload) отбрасывается. `fetchingRef` гейтит ТОЛЬКО
 * loadMore; запросы-«ресеты» (смена поиска/reload) форсят новый запрос
 * сквозь guard (force: true), чтобы новый поиск не терялся, пока летит
 * старый ответ. finally сбрасывает fetchingRef только у актуального seq.
 */
export function useInfiniteSelectList<T>(
  opts: InfiniteSelectListOptions<T>,
): InfiniteSelectListState<T> {
  const {
    fetchPage,
    getId,
    pageSize = 100,
    searchDebounceMs = 300,
    initialSearch = "",
  } = opts

  const [items, setItems] = useState<T[]>([])
  const [total, setTotal] = useState(0)
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [search, setSearch] = useState(initialSearch)
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch)
  const [reloadKey, setReloadKey] = useState(0)

  const seqRef = useRef(0)
  const fetchingRef = useRef(false)
  const totalRef = useRef(0)
  const fetchPageRef = useRef(fetchPage)
  fetchPageRef.current = fetchPage
  const getIdRef = useRef(getId)
  getIdRef.current = getId

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), searchDebounceMs)
    return () => clearTimeout(t)
  }, [search, searchDebounceMs])

  const runRequest = useCallback(
    (offset: number, searchValue: string, force: boolean) => {
      if (fetchingRef.current && !force) return // только loadMore ждёт
      const seq = ++seqRef.current
      fetchingRef.current = true
      setIsFetching(true)
      Promise.resolve()
        .then(() =>
          fetchPageRef.current({
            limit: pageSize,
            offset,
            search: searchValue || undefined,
          }),
        )
        .then((res) => {
          if (seq !== seqRef.current) return
          totalRef.current = res.total
          setTotal(res.total)
          setError(null)
          if (offset === 0) {
            setItems(res.items)
          } else {
            setItems((prev) => {
              const seen = new Set(prev.map((i) => getIdRef.current(i)))
              return [
                ...prev,
                ...res.items.filter((i) => !seen.has(getIdRef.current(i))),
              ]
            })
          }
        })
        .catch((e) => {
          if (seq !== seqRef.current) return
          setError(e)
        })
        .finally(() => {
          if (seq !== seqRef.current) return // актуальный запрос сбросит флаг
          fetchingRef.current = false
          setIsFetching(false)
        })
    },
    [pageSize],
  )

  // Первая страница / сброс при смене (debounced) поиска или reload.
  // force: true — не теряем новый поиск, пока летит старый запрос.
  useEffect(() => {
    runRequest(0, debouncedSearch, true)
  }, [debouncedSearch, reloadKey, runRequest])

  const loadMore = useCallback(() => {
    if (items.length >= totalRef.current) return
    runRequest(items.length, debouncedSearch, false)
  }, [items.length, debouncedSearch, runRequest])

  const reload = useCallback(() => setReloadKey((k) => k + 1), [])

  return {
    items,
    total,
    hasMore: items.length < total,
    isFetching,
    error,
    search,
    setSearch,
    loadMore,
    reload,
  }
}
